/**
 * Subscreen FacebookConnect
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Box, Typography, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import ConnectPages from '../components/ConnectPages'
import { useDispatch, useSelector } from 'react-redux'
import {
  InitiateConnectOperationRequest,
  PerformConnectOperationRequest,
  PerformDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SmPublishConnectApi'
import {
  getFacebookConnectDataInitiated,
  getSmPublishAuthTokenInitiated,
  initiateFacebookConnectInitiated,
  performConnectInitiated,
  performDisconnectInitiated,
  setConnectedFacebookPage,
  setConnectedInstagramPage,
  setFacebookConnectHash,
  setTokenChanged,
  socialmediaEditorConnectInitiated,
  socialmediaEditorDisconnectInitiated,
  socialmediaUserConnectInitiated,
  socialmediaUserDisconnectInitiated,
} from '../../../store/common/common.actions'
import {
  EUserRole,
  NetworkType,
  SocialmediaConnectionState,
} from '@professocialApi/professocial-core-frontend-api/models'
import { FacebookConnectDataElement, GetFacebookConnectDataRequest } from '@3m5/sm_publish-frontend-api'
import {
  getConnectedFacebookPage,
  getConnectionChanged,
  getFacebookConnectHash,
  getFacebookPages,
  getTokenChanged,
  getUser,
  getUserPages,
  namedRequestData,
} from '../../../store/common/common.selectors'
import { ISettingsConnectProps } from './SettingsConnect'
import {
  SocialmediaUserConnectRequest,
  SocialmediaUserDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SocialmediaUserApi'
import {
  SocialmediaEditorConnectRequest,
  SocialmediaEditorDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SocialmediaEditorApi'
import { useHistory } from 'react-router-dom'
import { CoreRootState } from '../../../store/coreRoot.reducer'
import useQuery from '../../../util/useQuery'
import {
  getPageMasterInitiated,
  listPageOwnersForPageInitiated,
  setFacebookPage,
  setUserAsPageMasterInitiated,
} from '../../../store/dashboard/dashboard.actions'
import {
  ListPageOwnersForPageRequest,
  SetUserAsPageMasterRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/UserHasPageApi'
import { GetPageMasterRequest, UserSettings } from '@professocialApi/professocial-core-frontend-api'
import { getPageOwnersData } from '../../../store/dashboard/dashboard.selector'
import { FacebookPage } from '@3m5/sm_publish-frontend-api'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import LinkIcon from '@mui/icons-material/Link'

const SettingsFacebookConnect: React.FC<ISettingsConnectProps> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const query = useQuery()
  const history = useHistory()
  const user: UserSettings | undefined = useSelector(getUser)
  const connectionChanged: boolean = useSelector(getConnectionChanged)
  const tokenChanged: boolean = useSelector(getTokenChanged)
  const facebookConnectHash: string | undefined = useSelector(getFacebookConnectHash)
  const facebookPages: FacebookConnectDataElement[] = useSelector(getFacebookPages) || []
  const pageOwnersList = useSelector(getPageOwnersData)
  const connectedUserPages = useSelector(getUserPages)
  const connectedPage: string | undefined = useSelector(getConnectedFacebookPage)
  const onInitiateConnect: ActionCall | undefined = useSelector(
    (state: CoreRootState) => namedRequestData(state, initiateFacebookConnectInitiated.type)
  )
  const [facebookConnection, setFacebookConnection] = useState<boolean>(false)
  const [disableConnection, setDisableConnection] = useState<boolean>(false)
  const [connectedFBPages, setConnectedFBPages] = useState<FacebookConnectDataElement[]>()
  const [availableFBPages, setAvailableFBPages] = useState<FacebookConnectDataElement[]>()

  const dispatchSetFacebookConnectHash = useCallback(
    (params: string | undefined) => dispatch(setFacebookConnectHash(params)),
    [dispatch]
  )
  const dispatchSetConnectedFacebookPage = useCallback(
    (params: string) => dispatch(setConnectedFacebookPage(params)),
    [dispatch]
  )
  const dispatchSetConnectedInstagramPage = useCallback(
    (params: string) => dispatch(setConnectedInstagramPage(params)),
    [dispatch]
  )
  const dispatchSocialmediaUserConnect = useCallback(
    (params: SocialmediaUserConnectRequest) => dispatch(socialmediaUserConnectInitiated(params)),
    [dispatch]
  )
  const dispatchSocialmediaUserDisconnect = useCallback(
    (params: SocialmediaUserDisconnectRequest) => dispatch(socialmediaUserDisconnectInitiated(params)),
    [dispatch]
  )
  const dispatchSocialmediaEditorConnect = useCallback(
    (params: SocialmediaEditorConnectRequest) => dispatch(socialmediaEditorConnectInitiated(params)),
    [dispatch]
  )
  const dispatchSocialmediaEditorDisconnect = useCallback(
    (params: SocialmediaEditorDisconnectRequest) => dispatch(socialmediaEditorDisconnectInitiated(params)),
    [dispatch]
  )
  const dispatchInitiateConnect = useCallback(
    (params: InitiateConnectOperationRequest) => dispatch(initiateFacebookConnectInitiated(params)),
    [dispatch]
  )
  const dispatchPerformConnect = useCallback(
    (params: PerformConnectOperationRequest) => dispatch(performConnectInitiated(params)),
    [dispatch]
  )
  const dispatchPerformDisconnect = useCallback(
    (params: PerformDisconnectRequest) => dispatch(performDisconnectInitiated(params)),
    [dispatch]
  )
  const dispatchGetFacebookConnectData = useCallback(
    (params: GetFacebookConnectDataRequest) => dispatch(getFacebookConnectDataInitiated(params)),
    [dispatch]
  )
  const dispatchGetToken = useCallback(() => dispatch(getSmPublishAuthTokenInitiated()), [dispatch])
  const dispatchListPageOwnersForPage = useCallback(
    (params: ListPageOwnersForPageRequest) => dispatch(listPageOwnersForPageInitiated(params)),
    [dispatch]
  )
  const dispatchGetPageMaster = useCallback(
    (params: GetPageMasterRequest) => dispatch(getPageMasterInitiated(params)),
    [dispatch]
  )
  const dispatchSetPageMaster = useCallback(
    (params: SetUserAsPageMasterRequest) => dispatch(setUserAsPageMasterInitiated(params)),
    [dispatch]
  )
  const dispatchSetFacebookPage = useCallback(
    (params: FacebookPage | undefined) => dispatch(setFacebookPage(params)),
    [dispatch]
  )
  const dispatchSetTokenChanged = useCallback(
    (params: boolean) => dispatch(setTokenChanged(params)),
    [dispatch]
  )

  // Memoized callback to get the owner data, so its reference doesn't change on every render
  const getOwnerData = useCallback((networkPageId: string) => {
    dispatchListPageOwnersForPage({
      networkPageId,
      networkType: NetworkType.Facebook,
    })
    dispatchGetPageMaster({
      networkPageId,
      networkType: NetworkType.Facebook,
    })
  }, [dispatchListPageOwnersForPage, dispatchGetPageMaster])

  const setPageOwner = (data: SetUserAsPageMasterRequest) => {
    dispatchSetPageMaster(data)
  }

  useEffect(() => {
    if (props.connection?.state === SocialmediaConnectionState.Connected) {
      setFacebookConnection(true)
      setDisableConnection(false)
      const params: InitiateConnectOperationRequest = {
        initiateConnectRequest: {
          networkType: NetworkType.Facebook,
        },
      }
      dispatchInitiateConnect(params)
      return
    }
    if (props.connection?.state === SocialmediaConnectionState.Invalid) {
      setDisableConnection(false)
      setFacebookConnection(true)
      return
    }
    if (props.connection?.state === SocialmediaConnectionState.Disabled) {
      setDisableConnection(true)
      setFacebookConnection(false)
    }
  }, [props.connection, dispatchInitiateConnect])

  useEffect(() => {
    if (!!facebookPages && facebookPages.length && connectedUserPages) {
      const filteredConnectedFBPages = facebookPages.filter(
        (fbp) =>
          fbp.isConnected &&
          !!connectedUserPages.find((cup) => cup.networkPageId === fbp.page.networkPageId)
      )
      if (filteredConnectedFBPages) {
        const availablePages = facebookPages.filter(
          (fbp) =>
            !filteredConnectedFBPages.find((cigp) => cigp.page.networkPageId === fbp.page.networkPageId)
        )
        setConnectedFBPages(filteredConnectedFBPages)
        setAvailableFBPages(availablePages)
      }
    }
  }, [facebookPages, connectedUserPages])

  useEffect(() => {
    const page = connectedUserPages && connectedUserPages.find((elem) => elem.networkType === 'Facebook')
    if (page) {
      dispatchSetConnectedFacebookPage(page.networkPageId)
    }
  }, [connectedUserPages, dispatchSetConnectedFacebookPage])

  useEffect(() => {
    dispatchSetFacebookConnectHash(facebookConnectHash || undefined)
  }, [tokenChanged, dispatchSetFacebookConnectHash, facebookConnectHash])

  useEffect(() => {
    if (facebookConnection && tokenChanged && query.get('login_as')) {
      history.push('/')
    }
  }, [tokenChanged, facebookConnection, history, query])

  useEffect(() => {
    dispatchGetToken()
    if (facebookConnectHash) {
      const params: GetFacebookConnectDataRequest = {
        connectHash: facebookConnectHash,
      }
      dispatchGetFacebookConnectData(params)
    }
  }, [facebookConnectHash, dispatchGetToken, dispatchGetFacebookConnectData])

  useEffect(() => {
    if (connectionChanged && facebookConnectHash) {
      const params: GetFacebookConnectDataRequest = {
        connectHash: facebookConnectHash,
      }
      dispatchGetFacebookConnectData(params)
    }
  }, [connectionChanged, facebookConnectHash, dispatchGetFacebookConnectData])

  return (
    <>
      <Typography sx={{ mb: 3, whiteSpace: 'break-spaces' }}>
        {facebookConnection
          ? props.connection?.state === SocialmediaConnectionState.Invalid
            ? t('coreSettings:fbInvalidConnectionDesc')
            : t('coreSettings:fbConnectionDesc')
          : t('coreSettings:noConnection')}
      </Typography>
      <PreloaderButton
        position="left"
        loading={onInitiateConnect?.inProgress}
        disableButton={disableConnection}
        onClick={() => {
          if (facebookConnection) {
            if (props.connection?.id && user) {
              dispatchSetConnectedInstagramPage('')
              dispatchSetConnectedFacebookPage('')
              switch (user.role) {
              case EUserRole.User: {
                const userParams: SocialmediaUserDisconnectRequest = {
                  connectionId: props.connection?.id,
                }
                dispatchSocialmediaUserDisconnect(userParams)
                setFacebookConnection(false)
                return
              }
              case EUserRole.Editor: {
                const editorParams: SocialmediaEditorDisconnectRequest = {
                  connectionId: props.connection?.id,
                }
                dispatchSocialmediaEditorDisconnect(editorParams)
                setFacebookConnection(false)
              }
              }
            }
          } else {
            if (props.connection?.id && user) {
              dispatchSetConnectedInstagramPage('')
              dispatchSetConnectedFacebookPage('')
              switch (user.role) {
              case EUserRole.User: {
                const userParams: SocialmediaUserConnectRequest = {
                  connectionId: props.connection?.id,
                  redirectUrl: window.location.href,
                }
                dispatchSocialmediaUserConnect(userParams)
                return
              }
              case EUserRole.Editor: {
                const editorParams: SocialmediaEditorConnectRequest = {
                  connectionId: props.connection?.id,
                  redirectUrl: window.location.href,
                }
                dispatchSocialmediaEditorConnect(editorParams)
              }
              }
            }
          }
        }}
        text={
          <Box sx={{ display: 'flex', alignItems: 'center', textTransform: 'initial' }}>
            <Typography sx={{ pr: 0.5 }}>{t('coreSettings:disconnectStart')}</Typography>
            <Typography>
              <b>profes</b>
              <i>social</i>
            </Typography>
            <Box sx={{ display: 'flex', fontSize: '0.6rem', alignSelf: 'start', pl: 0.2 }}>©</Box>
            <Typography sx={{ pl: 0.5 }}>
              {facebookConnection ? t('coreSettings:disconnectEnd') : t('coreSettings:connectEnd')}
            </Typography>
          </Box>
        }
      />
      {facebookConnection && facebookConnectHash && (
        <>
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {t('coreSettings:fbConnectedPages')}
                </Typography>
                {!!connectedFBPages && connectedFBPages.length ? (
                  connectedFBPages.map((facebookPage: FacebookConnectDataElement, index: number) => (
                    <ConnectPages
                      key={index}
                      disabled={false}
                      canAddPage={true}
                      handleGetOwnerData={getOwnerData}
                      handleSetPageOwner={setPageOwner}
                      pageOwnersList={pageOwnersList}
                      page={facebookPage}
                      networkType={NetworkType.Facebook}
                      disconnect={(networkPageId: string) => {
                        const params: PerformDisconnectRequest = {
                          performConnectRequest: {
                            connectHash: facebookConnectHash,
                            networkPageId: networkPageId,
                          },
                        }
                        dispatchSetConnectedFacebookPage('')
                        dispatchSetFacebookPage(undefined)
                        dispatchPerformDisconnect(params)
                      }}
                    />
                  ))
                ) : (
                  <NoDataComponent noDataText={t('coreSettings:noConnectedPages')} alignment="left" margin="16px 0" />
                )}
              </Grid>
              <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Typography variant="body1">
                  <b>Hinweis:</b>
                  <br />
                  <br />
                  {t('coreSettings:fbConnectOwnerHint')}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Typography variant="h4" sx={{ mt: 4 }}>
              {t('coreSettings:fbAvailablePages')}
              <LinkIcon sx={{ transform: 'translate(0, 6px)' }} />
              {t('coreSettings:fbAvailablePages2')}
            </Typography>
            {!!availableFBPages && availableFBPages.length ? (
              availableFBPages.map((facebookPage: FacebookConnectDataElement, index: number) => (
                <ConnectPages
                  key={index}
                  disabled={!!connectedPage && !!user?.canAddFacebookPage}
                  canAddPage={user?.canAddFacebookPage}
                  page={facebookPage}
                  networkType={NetworkType.Facebook}
                  connect={(networkPageId: string) => {
                    const params: PerformConnectOperationRequest = {
                      performConnectRequest: {
                        connectHash: facebookConnectHash,
                        networkPageId: networkPageId,
                      },
                    }
                    dispatchSetConnectedFacebookPage(networkPageId)
                    dispatchSetTokenChanged(false)
                    dispatchPerformConnect(params)
                  }}
                />
              ))
            ) : (
              <NoDataComponent noDataText={t('coreSettings:noConnectedPages')} alignment="left" margin="16px 0" />
            )}
          </Box>
        </>
      )}
    </>
  )
}

export default SettingsFacebookConnect
